import { parseCsvCells } from '@/features/csvImport/admin/utils/parseCsvCells';
import { readCsvLines } from '@/features/csvImport/admin/utils/readCsvLines';

/**
 * Why a file cannot be imported at all, in the vocabulary the bulk flows share. Each flow maps
 * these onto its own copy — and, where there is a server behind it, onto that server's error
 * codes — so an admin is told the same thing whether the file was turned down here or there.
 */
export type CsvFileIssue = 'invalidCsv' | 'templateMismatch' | 'tooLarge';

/** Either the file is importable and its rows are counted, or it is refused for one reason. */
export type CsvFileCheck = { rowCount: number; issue?: never } | { rowCount?: never; issue: CsvFileIssue };

type CsvFileCheckOptions = {
  /** The column names the file has to carry, in this order. */
  templateColumns: string[];
  /** Most data rows the server will take in one file. */
  maxRows: number;
};

const parseHeaderColumns = (headerLine: string): string[] => {
  const columns = parseCsvCells(headerLine);

  // A row ending in a comma leaves an empty cell behind that no column answers to.
  while (columns.length && columns[columns.length - 1] === '') columns.pop();

  return columns;
};

/**
 * Compared case-insensitively and free of padding: the server is the authority on the header,
 * so erring towards accepting a file it would accept costs a round trip, while erring the
 * other way blocks an import that should have gone through.
 */
const matchesTemplate = (headerLine: string, templateColumns: string[]): boolean => {
  const columns = parseHeaderColumns(headerLine);

  return (
    columns.length === templateColumns.length &&
    columns.every((column, index) => column.toLowerCase() === templateColumns[index].toLowerCase())
  );
};

/**
 * The file-level checks the server also runs — is this a CSV, does it match the template, is it
 * within the row cap — answered before the file is uploaded.
 *
 * Not a substitute for the server's own answer, which still decides on Continue. This is here
 * so a file that cannot pass is never staged in the first place: the flow is specified as
 * blocking the upload outright, rather than accepting the file and failing a later click.
 */
export const checkCsvFile = async (file: File, { templateColumns, maxRows }: CsvFileCheckOptions): Promise<CsvFileCheck> => {
  let lines: string[];

  try {
    lines = await readCsvLines(file);
  } catch {
    return { issue: 'invalidCsv' };
  }

  const [headerLine] = lines;

  /**
   * Nothing to read a header from: an empty file, or one holding only blank lines. A file with no
   * header carries none of the template's columns, so it is turned down the same way as one whose
   * header names the wrong ones — and that is the message that says what to do about it. Only a
   * file that could not be read at all is left to `invalidCsv`.
   */
  if (!headerLine) return { issue: 'templateMismatch' };

  if (!matchesTemplate(headerLine, templateColumns)) return { issue: 'templateMismatch' };

  const rowCount = lines.length - 1;

  if (rowCount > maxRows) return { issue: 'tooLarge' };

  return { rowCount };
};
